import { useCallback, useEffect, useState } from 'react';

import {
	TableResponse,
	TProject,
	TTableColumns,
} from '@/components/tables/components/dto';
import { dateToRFC, newDateFormat } from '@/helpers/dates';
import { useApi } from '@/hooks/useApi/useApi';
import useEffectOnlyOnUpdate from '@/hooks/useEffectOnlyOnUpdate/useEffectOnlyOnUpdate';
import { PatchDataType, useApiTableType } from '@/types';

const useApiTable = (
	url: string,
	params?: URLSearchParams,
	testData?: () => TableResponse,
	updateTable?: boolean,
	setUpdateTable?: React.Dispatch<React.SetStateAction<boolean>>
): useApiTableType => {
	const { apiGet, apiPatch, apiDelete } = useApi();
	const [columns, setColumns] = useState<TTableColumns[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState<TProject[]>([]);
	const [pages, setPages] = useState({
		current_page_number: 1,
		items_per_page: 1,
		total_pages: 1,
		total_items: 1,
	});

	const fetchTable = useCallback(async () => {
		setIsLoading(true);

		if (testData) {
			setColumns(testData().columns);
			setData(testData().data);
		} else {
			try {
				const res = await (pages.current_page_number > 1
					? apiGet(`${url}&page=${pages.current_page_number}`, params)
					: apiGet(url, params));

				setColumns(res.data.columns);
				setData(res.data.data);
				setPages({
					current_page_number: res.data.current_page_number,
					items_per_page: res.data.items_per_page,
					total_pages: res.data.total_pages,
					total_items: res.data.total_items,
				});
			} catch (error) {
				console.error(error);
			}
		}
		setIsLoading(false);
		if (updateTable && setUpdateTable) setUpdateTable(false);
	}, [
		testData,
		params,
		updateTable,
		pages.current_page_number,
		url,
		setUpdateTable,
		apiGet,
	]);

	const sortTable = useCallback(
		async (column: string) => {
			setIsLoading(true);

			let newUrl = `${url}`;

			newUrl += url.includes('?')
				? `&ordering=${column}`
				: `?ordering=${column}`;

			const res = await apiGet(newUrl, params);

			setData(res.data.data);

			setIsLoading(false);
		},
		[url, apiGet, params]
	);

	useEffect(() => {
		fetchTable();
	}, [params, pages.current_page_number, url]);

	useEffectOnlyOnUpdate(() => {
		if (updateTable) fetchTable();
	}, [updateTable]);

	const updateData = useCallback(
		async (
			taskId: number,
			patchValues: PatchDataType | null,
			cell?: number,
			value?: unknown
		) => {
			let patchData: PatchDataType = {};

			if (patchValues) {
				patchData = patchValues;
			} else {
				switch (cell) {
					case 1: {
						patchData.task = value;
						break;
					}
					case 2: {
						patchData.project = value;
						break;
					}
					case 3: {
						patchData.date = dateToRFC(newDateFormat(value as string));
						break;
					}
					case 4: {
						patchData.action_type = value;
						break;
					}
					case 5: {
						patchData.spent_time_min = value;
						break;
					}
					case 6: {
						patchData.task_status = value;
						break;
					}
				}
			}

			return await apiPatch(
				`${import.meta.env.VITE_API_URL}api/project/project_time_tracking/${taskId}/`,
				patchData,
				params
			);
		},
		[params, apiPatch]
	);

	const deleteData = useCallback(
		async (taskId: number) => {
			return await apiDelete(
				`${import.meta.env.VITE_API_URL}api/project/project_time_tracking/${taskId}/`,
				params
			);
		},
		[params, apiDelete]
	);

	return {
		columns,
		data,
		pages,
		isLoading,
		sortTable,
		updateData,
		deleteData,
		setPages,
	};
};

export default useApiTable;
